// src/views/FlaggedInbox.js
// PHASE 1.6 VERIFIER: r1-16-web-inbox-16a3f2b8
//
// Phase 1.6 — Flagged-note triage inbox.
//
// Live view of unresolved flagged notes assigned to the current user. Source
// of truth is the notes collection (NOT the notifications subcollection):
// notifications and flag state can drift — a notification can be read while
// the flag is still active; a reassign moves the flag without affecting the
// old target's read notifications; an unflag deletes notifications but the
// note doc still exists with flagHistory. The notes collection is canonical
// for "who owns this flag right now."
//
// Query:
//   organizations/{orgId}/notes
//   where flagTargetUserId == currentUid
//   where flagged == true
//   where resolved == false
//   orderBy addedAt desc
//
// Composite index: (flagTargetUserId asc, flagged asc, resolved asc, addedAt desc)
// Single-collection scope (NOT collection-group). Built in Firebase Console
// per sub-batch 1.
//
// Permission posture:
//   - firestore.rules: any org member can list/read any note in their org
//     (existing rule, unchanged for 1.6). The query already filters to
//     flagTargetUserId == uid, so users only ever see their own flags.
//     Defense-in-depth: even if the query were tampered with on the client,
//     the rule still gates on org membership.
//
// orgUsers prop is wired through but not currently consumed — Reassign was
// intentionally kept off the inbox row per the Phase 1.6 design discussion
// (Open → NoteCard if Reassign is needed). Prop is accepted now so the
// wiring is in place if Reassign ever joins the row actions later.

import React, { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Flag } from 'lucide-react';
import FlaggedInboxRow from '../components/crm/FlaggedInboxRow';

// eslint-disable-next-line no-unused-vars
const FlaggedInbox = ({ currentUserProfile, orgUsers, orgId }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const uid = currentUserProfile?.id;

  useEffect(() => {
    if (!uid || !orgId) {
      setLoading(false);
      return undefined;
    }

    const q = query(
      collection(db, `organizations/${orgId}/notes`),
      // FO-4 multi-reviewer: reviewer group, not single target.
      where('flagReviewerIds', 'array-contains', uid),
      where('flagged', '==', true),
      where('resolved', '==', false),
      orderBy('addedAt', 'desc'),
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        setNotes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
        setError(null);
      },
      (err) => {
        // Common cause on first load: composite index still building / not
        // created. The error message includes a console URL to create it.
        // Once built, the listener auto-recovers.
        console.error('FlaggedInbox subscription error:', err);
        setError(err?.message || 'Could not load flagged notes.');
        setLoading(false);
      },
    );

    return unsub;
  }, [uid, orgId]);

  if (!uid || !orgId) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <p className="text-sm text-slate-500">
          Sign in to view your flagged notes.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      <header className="mb-4 md:mb-6">
        <div className="flex items-center gap-2">
          <Flag size={22} className="text-amber-500" />
          <h1 className="text-2xl font-bold text-slate-900">
            Flagged for you
          </h1>
        </div>
        <p className="text-sm text-slate-500 mt-1">
          {loading
            ? 'Loading…'
            : notes.length === 0
              ? 'No flags to triage.'
              : `${notes.length} unresolved flag${notes.length === 1 ? '' : 's'}.`}
        </p>
      </header>

      {loading && (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-24 bg-slate-100 rounded-xl animate-pulse"
            />
          ))}
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <p className="font-semibold mb-1">Couldn&rsquo;t load flagged notes.</p>
          <p className="text-xs">{error}</p>
        </div>
      )}

      {!loading && !error && notes.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
          <Flag size={36} className="text-slate-300 mx-auto mb-3" />
          <p className="text-base font-semibold text-slate-700">
            Nothing flagged for you
          </p>
          <p className="text-sm text-slate-500 mt-1">
            When teammates flag a note for you, it&rsquo;ll show up here.
          </p>
        </div>
      )}

      {!loading && !error && notes.length > 0 && (
        <div className="space-y-2">
          {notes.map((note) => (
            <FlaggedInboxRow
              key={note.id}
              note={note}
              currentUserProfile={currentUserProfile}
              orgUsers={orgUsers}
              orgId={orgId}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default FlaggedInbox;